import { motion } from 'framer-motion';
import MatchingQuestion from './MatchingQuestion';

const QuestionCard = ({
  quizType,
  question,
  answer,
  questionNumber,
  onOptionSelect,
  onMultipleSelect,
  onTheoryChange,
  onMatchingChange,
  direction,
  checkAnswer,
  onUncheckAnswer,
  onCheckAnswer
}) => {

  const showFeedback = checkAnswer || false;

  const OPTION_CLASSES = {
    normal: "border-gray-200 hover:border-primary/30",
    selected: "border-primary bg-primary/5",
    correct: "border-green-500 bg-green-200",
    wrong: "border-red-500 bg-red-200"
  };

  const selectedOption = answer?.selected_option || null;
  const theoryAnswer = answer?.theory_answer || '';
  const selectedOptions = answer?.selected_options || [];

  let correctOptionIds = [];
  let isMultipleSelectFullyCorrect = false;

  if (['objective', 'multiple_select'].includes(question.question_type)) {
    correctOptionIds = question.options
      .filter(opt => opt.is_correct)
      .map(opt => opt.id);

    isMultipleSelectFullyCorrect =
      selectedOptions.length === correctOptionIds.length &&
      selectedOptions.every(id => correctOptionIds.includes(id));
  }

  const getOptionClasses = (option) => {
    const isSelected = selectedOption === option.id;
    if (!isSelected) return OPTION_CLASSES.normal;

    if (quizType === "learning" && showFeedback) {
      const correctOption = question.options.find(opt => opt.is_correct);
      const isCorrect = option.id === correctOption.id;
      return isCorrect ? OPTION_CLASSES.correct : OPTION_CLASSES.wrong;
    }

    return OPTION_CLASSES.selected;
  };

  const getMultiOptionClasses = (option) => {
    const isSelected = selectedOptions.includes(option.id);
    if (!isSelected) return OPTION_CLASSES.normal;

    if (quizType === "learning" && showFeedback) {
      return option.is_correct ? OPTION_CLASSES.correct : OPTION_CLASSES.wrong;
    }

    return OPTION_CLASSES.selected;
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      position: "relative",
      width: "100%",
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
    })
  };

  const isCheckDisabled = () => {
    if (question.question_type === 'objective') {
      return !selectedOption;
    }
    if (question.question_type === 'multiple_select') {
      return !selectedOptions || selectedOptions.length === 0;
    }
    if (question.question_type === 'matching') {
      return !answer?.matching_answers || answer.matching_answers.length < question.matching_pair.length;
    }
    return false;
  };

  return (
    <motion.div
      className="w-full"
      custom={direction}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      layout
      transition={{
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 }
      }}
    >
      <div className="w-full bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-medium mb-4">
          {questionNumber}. {question.text}
        </h2>

        {question.image && (
          <div className="mb-4 flex justify-center">
            <img
              src={question.image}
              alt="Question"
              className="max-h-56 rounded border shadow"
              style={{ objectFit: 'contain', maxWidth: '100%' }}
            />
          </div>
        )}

        {/* Objective Question */}
        {question.question_type === 'objective' && (
          <div className="space-y-3">
            {question.options.map((option) => (
              <label
                key={option.id}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${getOptionClasses(option)}`}
              >
                <input
                  type="radio"
                  name="answer"
                  className="radio radio-primary mr-3"
                  checked={selectedOption === option.id}
                  disabled={showFeedback && quizType == "learning"} // Disable after selection in learning mode
                  onChange={() => {
                    onOptionSelect(option.id);
                    onUncheckAnswer(question.id);
                  }}
                />
                <div className="flex items-center justify-between gap-2 flex-1">
                  <span>{option.text}</span>
                  {quizType === "learning" && showFeedback && selectedOption === option.id && (
                    option.is_correct
                      ? <span className="text-success font-semibold flex items-center gap-1">✓ <span>Good!</span></span>
                      : <span className="text-error font-semibold flex items-center gap-1">✗ <span>Wrong</span></span>
                  )}
                </div>
              </label>
            ))}
            {quizType === "learning" && showFeedback && selectedOption && (
              <div className="mt-3">
                {(() => {
                  const selected = question.options.find(opt => opt.id === selectedOption);
                  if (!selected?.explanation) return null;
                  const colorClass = selected.is_correct
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800";
                  return (
                    <div className={`p-3 rounded border ${colorClass}`}>
                      <strong>Explanation:</strong> {selected.explanation}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Multiple Select Question */}
        {question.question_type === 'multiple_select' && (
          <div className="space-y-3">
            {question.options.map((option) => (
              <label
                key={option.id}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${getMultiOptionClasses(option)}`}
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary mr-3"
                  checked={selectedOptions.includes(option.id)}
                  disabled={ showFeedback && quizType == 'learning' }// Disable after selection in learning mode
                  onChange={() => {
                    onMultipleSelect(option.id);
                    onUncheckAnswer(question.id);
                  }}
                />
                <span>{option.text}</span>
              </label>
            ))}

            {quizType === "learning" && showFeedback && (
              <div className="mt-4">
                {isMultipleSelectFullyCorrect ? (
                  <div className="p-3 rounded bg-green-50 text-green-800 border border-green-200">
                    <strong>Correct!</strong> You selected all the right options.
                    <div className="mt-2 space-y-2">
                      {question.options.filter(opt => opt.is_correct && opt.explanation).map(opt => (
                        <div key={opt.id} className="p-2 bg-gray-50 rounded border border-gray-200">
                          <strong>{opt.text}:</strong> {opt.explanation}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-3 rounded bg-red-50 text-red-800 border border-red-200">
                    <strong>Incorrect.</strong> Review the options and try again.
                    {question.options
                      .filter(opt => selectedOptions.includes(opt.id) && !opt.is_correct && opt.explanation)
                      .map(opt => (
                        <div key={opt.id} className="mt-2 p-2 bg-gray-50 text-gray-800 border border-gray-200 rounded">
                          <strong>{opt.text}:</strong> {opt.explanation}
                        </div>
                      ))}

                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Theory Question */}
        {question.question_type === 'theory' && (
          <div className="space-y-4">
            <textarea
              className="textarea textarea-bordered w-full h-32"
              placeholder="Type your answer here..."
              disabled={quizType === "learning" && showFeedback}
              value={theoryAnswer}
              onChange={(e) => onTheoryChange(e.target.value)}
            />
            {quizType === "learning" && showFeedback && (
              <div
                className={`p-3 rounded border ${theoryAnswer.trim().toLowerCase() === question.answer.trim().toLowerCase()
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
                  }`}
              >
                <strong>{theoryAnswer.trim().toLowerCase() === question.answer.trim().toLowerCase() ? "Correct!" : "Incorrect"}</strong>
                <p className="mt-1"><strong>Correct Answer:</strong> {question.answer}</p>
              </div>
            )}
          </div>
        )}

        {/* Matching Question */}
        {question.question_type === "matching" && (
          <MatchingQuestion
            question={question}
            answer={answer}
            quizType={quizType}
            onMatchingChange={(pairId, selectedRight) => {
              onMatchingChange(pairId, selectedRight);
              onUncheckAnswer(question.id);
            }}
            showFeedback={showFeedback}
          />
        )}

        {/* Check Answer Button */}
        {quizType === "learning" && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => onCheckAnswer(question.id)}
              className="btn btn-accent"
              disabled={isCheckDisabled()}
            >
              Check Answer
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default QuestionCard;
